import { Component, Injector, Input } from '@angular/core';
import { filter, finalize, switchMap } from 'rxjs/operators';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ActionPendingCatalog } from 'src/app/common/enumerations/enumerations';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { DisapprovalReasonComponent } from 'src/app/controls/disapproval-reason/disapproval-reason.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { HomePendingApi, HomePendingChange, HomePendingField } from './home-pending-change';
import { HomePendingChangeService } from './home-pending-change.service';

@Component({
  selector: 'home-pending-change',
  templateUrl: './home-pending-change.component.html',
  styleUrls: ['./home-pending-change.component.scss']
})
export class HomePendingChangeComponent extends BaseComponent {

  @Input() pendingChange?: HomePendingChange<any> | null;
  @Input() api!: HomePendingApi;
  @Input() approvePrivilege!: PrivilegeEnum;
  @Input() changes: HomePendingField[] = [];
  @Input() deleteLabel = 'Pending Deletion';

  busy = false;

  constructor(
    protected override injector: Injector,
    private pendingChangeService: HomePendingChangeService
  ) {
    super(injector);
  }

  get isDelete(): boolean {
    return this.pendingChange?.action == ActionPendingCatalog.Delete;
  }

  get title(): string {
    switch (this.pendingChange?.action) {
      case ActionPendingCatalog.Create: return 'New - Pending Approval';
      case ActionPendingCatalog.Delete: return this.deleteLabel;
      default: return 'Changes Pending Approval';
    }
  }

  get isSubmitter(): boolean {
    return !!this.pendingChange && this.pendingChange.createdBy == this.currentUser?.id;
  }

  /** Approvers need the approve privilege and cannot review their own submission (also enforced by the API). */
  get canApprove(): boolean {
    return !!this.pendingChange && !this.isSubmitter && this.hasPrivilege(this.approvePrivilege);
  }

  /** Applies the pending change; the Home panel refreshes through SignalR. */
  approve() {
    if (!this.pendingChange) return;
    this.busy = true;
    this.pendingChangeService.approve(this.api, this.pendingChange.id)
      .pipe(finalize(() => this.busy = false))
      .subscribe({
        next: () => this.alert.success('Change approved!'),
        error: error => this.showError(error)
      });
  }

  /** Asks for a reason, then rejects the pending change. */
  disapprove() {
    if (!this.pendingChange) return;
    const pendingId = this.pendingChange.id;
    this.dialog.open(DisapprovalReasonComponent, { width: '500px' }).afterClosed()
      .pipe(
        filter(reason => !!reason),
        switchMap(reason => {
          this.busy = true;
          return this.pendingChangeService.disapprove(this.api, pendingId, reason).pipe(finalize(() => this.busy = false));
        })
      )
      .subscribe({
        next: () => this.alert.warning('Change disapproved!'),
        error: error => this.showError(error)
      });
  }

  /** Lets the submitter withdraw their own pending change after confirming. */
  cancel() {
    if (!this.pendingChange) return;
    const pendingId = this.pendingChange.id;
    this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: { message: 'Are you sure you want to cancel this pending change?', icon: 'question' }
    }).afterClosed()
      .pipe(
        filter(confirmed => !!confirmed),
        switchMap(() => {
          this.busy = true;
          return this.pendingChangeService.cancel(this.api, pendingId).pipe(finalize(() => this.busy = false));
        })
      )
      .subscribe({
        next: () => this.alert.warning('Pending change cancelled!'),
        error: error => this.showError(error)
      });
  }

  private showError(error: any) {
    // The API returns a readable message for permission and stale-request failures.
    if (typeof error?.error === 'string' && error.error.length) this.alert.error(error.error);
    else this.alert.defaultError();
  }
}
